// src/pages/Home/StoreMaster.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Container,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  InputAdornment,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import * as XLSX from 'xlsx';
import config from '../../config';

const UNITS = ['ml', 'ltr', 'gm', 'kg', 'pcs', 'nos', 'bottle'];
const BOTTLE_BASE_UNITS = ['ml', 'gm'];

const empty = {
  storeName: '',
  unit: 'ml',
  bottleSize: '',
  // Backend column is BottleUnit — keep the form key aligned so payload mapping is trivial.
  bottleUnit: 'ml',
  currentStock: '',
  minStockAlert: '',
  activeStatus: true,
  // ─── Item Master extension fields ───────────────────────────────────
  itemCode: '', // auto-generated by backend if blank
  barcode: '',
  purchasePrice: '',
  sellingPrice: '',
  gstPercent: '',
  hsnCode: '',
  openingStock: '',
};

/**
 * Splits a fractional bottle quantity into whole bottles + leftover base units.
 *   formatBottleStock(99.76, 750, 'ml') -> { fullBottles: 99, leftover: 570, baseUnit: 'ml', totalBase: 74820 }
 * Floating-point safe: rounds the leftover to the nearest base unit to avoid
 * ugly numbers like 569.9999 from 0.76 * 750.
 */
const formatBottleStock = (currentStock, bottleSize, baseUnit = 'ml') => {
  const stock = Number(currentStock) || 0;
  const size = Number(bottleSize) || 0;
  if (size <= 0) {
    return {
      fullBottles: Math.floor(stock),
      leftover: 0,
      baseUnit,
      totalBase: 0,
    };
  }
  const totalBase = Math.round(stock * size);
  const fullBottles = Math.floor(totalBase / size);
  const leftover = totalBase - fullBottles * size;
  return { fullBottles, leftover, baseUnit, totalBase };
};

export default function StoreMasterPage() {
  const company = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');
  const token = sessionStorage.getItem('token');

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [form, setForm] = useState(empty);
  const [editOpen, setEditOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState(null);

  const fetch = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${config.API_BASE_URL}/home/GetStoreItems/${company.id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setItems(res.data || []);
    } catch {
      setAlert({ type: 'error', msg: 'Failed to load store items' });
    }
    setLoading(false);
  };

  useEffect(() => {
    fetch();
  }, []);

  const save = async () => {
    if (!form.storeName || !form.unit)
      return setAlert({ type: 'warning', msg: 'Name and Unit required' });
    setSaving(true);
    try {
      const isBottle = form.unit === 'bottle';
      if (isBottle && (!form.bottleSize || parseFloat(form.bottleSize) <= 0)) {
        setSaving(false);
        return setAlert({
          type: 'warning',
          msg: 'Enter the bottle size (e.g. 750 ml) so variant consumption can be deducted.',
        });
      }
      const payload = {
        ...form,
        companyId: company.id,
        currentStock: parseFloat(form.currentStock) || 0,
        minStockAlert: parseFloat(form.minStockAlert) || 0,
        bottleSize: isBottle ? parseFloat(form.bottleSize) || 0 : null,
        bottleUnit: isBottle ? form.bottleUnit || 'ml' : null,
        // Item Master extension fields — convert empty strings to null so
        // backend doesn't store "0" in place of "unspecified".
        itemCode: form.itemCode?.trim() || null,
        barcode: form.barcode?.trim() || null,
        purchasePrice:
          form.purchasePrice === '' ? null : parseFloat(form.purchasePrice),
        sellingPrice:
          form.sellingPrice === '' ? null : parseFloat(form.sellingPrice),
        gstPercent:
          form.gstPercent === '' ? null : parseFloat(form.gstPercent),
        hsnCode: form.hsnCode?.trim() || null,
        openingStock:
          form.openingStock === '' ? null : parseFloat(form.openingStock),
      };
      if (form.id)
        await axios.post(
          `${config.API_BASE_URL}/home/UpdateStoreItem`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } },
        );
      else
        await axios.post(`${config.API_BASE_URL}/home/AddStoreItem`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      setAlert({ type: 'success', msg: 'Saved successfully' });
      setForm(empty);
      setEditOpen(false);
      fetch();
    } catch {
      setAlert({ type: 'error', msg: 'Save failed' });
    }
    setSaving(false);
  };

  const toggle = async (id) => {
    await axios.get(`${config.API_BASE_URL}/home/ToggleStoreItem/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    fetch();
  };

  const exportExcel = () => {
    const ws = XLSX.utils.json_to_sheet(
      filtered.map((x, i) => {
        let stockDisplay;
        if (x.unit === 'bottle' && x.bottleSize) {
          const { fullBottles, leftover, baseUnit } = formatBottleStock(
            x.currentStock,
            x.bottleSize,
            x.bottleUnit || 'ml',
          );
          stockDisplay =
            leftover > 0
              ? `${fullBottles} bottle ${leftover} ${baseUnit}`
              : `${fullBottles} bottle`;
        } else {
          stockDisplay = `${Number(x.currentStock).toFixed(0)} ${x.unit}`;
        }
        return {
          '#': i + 1,
          Name: x.storeName,
          Unit: x.unit,
          Stock: stockDisplay,
          MinAlert: x.minStockAlert,
          Status: x.activeStatus ? 'Active' : 'Inactive',
        };
      }),
    );
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Store');
    XLSX.writeFile(wb, 'StoreMaster.xlsx');
  };

  const filtered = items.filter((x) =>
    x.storeName?.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <Container maxWidth='lg' sx={{ mt: 4, mb: 6 }}>
      {alert && (
        <Alert
          severity={alert.type}
          onClose={() => setAlert(null)}
          sx={{ mb: 2 }}>
          {alert.msg}
        </Alert>
      )}

      {/* Add Form */}
      <Card sx={{ borderRadius: 3, boxShadow: 3, mb: 4 }}>
        <CardContent>
          <Typography variant='h6' fontWeight={700} color='primary' mb={2}>
            Add Store Item
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Box display='flex' flexWrap='wrap' gap={2}>
            <TextField
              label='Item Name *'
              size='small'
              value={form.storeName}
              onChange={(e) => setForm({ ...form, storeName: e.target.value })}
              sx={{ minWidth: 220 }}
            />
            <TextField
              select
              label='Unit *'
              size='small'
              value={form.unit}
              onChange={(e) => setForm({ ...form, unit: e.target.value })}
              sx={{ minWidth: 120 }}
              SelectProps={{ native: true }}>
              {UNITS.map((u) => (
                <option key={u} value={u}>
                  {u}
                </option>
              ))}
            </TextField>
            {form.unit === 'bottle' && (
              <>
                <TextField
                  label='Bottle Size *'
                  size='small'
                  type='number'
                  value={form.bottleSize}
                  onChange={(e) =>
                    setForm({ ...form, bottleSize: e.target.value })
                  }
                  helperText='e.g. 750 (for a 750 ml bottle)'
                  sx={{ minWidth: 140 }}
                />
                <TextField
                  select
                  label='Bottle Base Unit'
                  size='small'
                  value={form.bottleUnit}
                  onChange={(e) =>
                    setForm({ ...form, bottleUnit: e.target.value })
                  }
                  sx={{ minWidth: 110 }}
                  SelectProps={{ native: true }}>
                  {BOTTLE_BASE_UNITS.map((u) => (
                    <option key={u} value={u}>
                      {u}
                    </option>
                  ))}
                </TextField>
              </>
            )}
            <TextField
              label={`Opening Stock${form.unit === 'bottle' ? ' (in bottles)' : ''}`}
              size='small'
              type='number'
              value={form.currentStock}
              onChange={(e) =>
                setForm({ ...form, currentStock: e.target.value })
              }
              sx={{ minWidth: 150 }}
            />
            <TextField
              label='Min Alert Level'
              size='small'
              type='number'
              value={form.minStockAlert}
              onChange={(e) =>
                setForm({ ...form, minStockAlert: e.target.value })
              }
              sx={{ minWidth: 150 }}
            />
            <Button
              variant='contained'
              startIcon={
                saving ? (
                  <CircularProgress size={14} color='inherit' />
                ) : (
                  <AddIcon />
                )
              }
              onClick={save}
              disabled={saving}
              sx={{ alignSelf: 'center' }}>
              {saving ? 'Saving...' : 'Add Item'}
            </Button>
          </Box>

          {/* Item Master extension fields — collapsed into a single row to
              keep the add form short while still capturing pro-grade data. */}
          <Box sx={{ mt: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <TextField
              label='Item Code'
              size='small'
              value={form.itemCode}
              onChange={(e) => setForm({ ...form, itemCode: e.target.value })}
              placeholder='Auto (ITM-####)'
              sx={{ minWidth: 140 }}
            />
            <TextField
              label='Barcode'
              size='small'
              value={form.barcode}
              onChange={(e) => setForm({ ...form, barcode: e.target.value })}
              sx={{ minWidth: 160 }}
            />
            <TextField
              label='Purchase Price'
              size='small'
              type='number'
              value={form.purchasePrice}
              onChange={(e) =>
                setForm({ ...form, purchasePrice: e.target.value })
              }
              sx={{ minWidth: 140 }}
            />
            <TextField
              label='Selling Price'
              size='small'
              type='number'
              value={form.sellingPrice}
              onChange={(e) =>
                setForm({ ...form, sellingPrice: e.target.value })
              }
              sx={{ minWidth: 140 }}
            />
            <TextField
              label='GST %'
              size='small'
              type='number'
              value={form.gstPercent}
              onChange={(e) =>
                setForm({ ...form, gstPercent: e.target.value })
              }
              sx={{ minWidth: 110 }}
            />
            <TextField
              label='HSN Code'
              size='small'
              value={form.hsnCode}
              onChange={(e) => setForm({ ...form, hsnCode: e.target.value })}
              sx={{ minWidth: 130 }}
            />
            <TextField
              label='Opening Stock'
              size='small'
              type='number'
              value={form.openingStock}
              onChange={(e) =>
                setForm({ ...form, openingStock: e.target.value })
              }
              placeholder='Defaults to Current Stock'
              sx={{ minWidth: 170 }}
            />
          </Box>
          {form.unit === 'bottle' && (
            <Typography
              variant='caption'
              color='text.secondary'
              sx={{ mt: 1, display: 'block' }}>
              Stock is tracked in bottles. When variants (e.g. 30 ml peg) are
              ordered, the system converts {form.bottleSize || '?'}{' '}
              {form.bottleUnit || 'ml'} per bottle automatically — so 4 × 30 ml
              deducts 120 / {form.bottleSize || '750'} ={' '}
              {form.bottleSize
                ? (120 / parseFloat(form.bottleSize)).toFixed(2)
                : '0.16'}{' '}
              bottles.
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Table */}
      <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
        <CardContent>
          <Box
            display='flex'
            justifyContent='space-between'
            alignItems='center'
            mb={2}>
            <Typography variant='h6' fontWeight={600}>
              Store Items
            </Typography>
            <Box display='flex' gap={1}>
              <TextField
                size='small'
                placeholder='Search...'
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <SearchIcon fontSize='small' />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                variant='outlined'
                startIcon={<FileDownloadIcon />}
                onClick={exportExcel}>
                Export
              </Button>
            </Box>
          </Box>
          {loading ? (
            <Box textAlign='center' py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table size='small'>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8fafc' }}>
                    {[
                      '#',
                      'Item Name',
                      'Unit',
                      'Current Stock',
                      'Min Alert',
                      'Status',
                      'Actions',
                    ].map((h) => (
                      <TableCell key={h} sx={{ fontWeight: 700 }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.map((item, i) => {
                    const isBottle =
                      item.unit === 'bottle' && Number(item.bottleSize) > 0;
                    const split = isBottle
                      ? formatBottleStock(
                          item.currentStock,
                          item.bottleSize,
                          item.bottleUnit || 'ml',
                        )
                      : null;
                    const lowStock =
                      Number(item.currentStock) <=
                      Number(item.minStockAlert || 0);

                    return (
                      <TableRow
                        key={item.id}
                        hover
                        sx={{
                          bgcolor: lowStock ? '#FFF8E1' : 'inherit',
                        }}>
                        <TableCell>{i + 1}</TableCell>
                        <TableCell fontWeight={600}>
                          {item.storeName}
                          {isBottle ? (
                            <Typography
                              variant='caption'
                              display='block'
                              color='text.secondary'>
                              {item.bottleSize} {item.bottleUnit || 'ml'} per
                              bottle
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell>
                          <Chip label={item.unit} size='small' />
                        </TableCell>
                        <TableCell>
                          {isBottle ? (
                            <>
                              <Typography
                                fontWeight={700}
                                color={lowStock ? 'error' : 'success.main'}>
                                {split.fullBottles} bottle
                                {split.leftover > 0
                                  ? ` ${split.leftover} ${split.baseUnit}`
                                  : ''}
                              </Typography>
                              <Typography
                                variant='caption'
                                color='text.secondary'>
                                = {split.totalBase} {split.baseUnit} total
                              </Typography>
                            </>
                          ) : (
                            <Typography
                              fontWeight={700}
                              color={lowStock ? 'error' : 'success.main'}>
                              {Number(item.currentStock).toFixed(0)} {item.unit}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {item.minStockAlert} {item.unit}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={item.activeStatus ? 'Active' : 'Inactive'}
                            color={item.activeStatus ? 'success' : 'error'}
                            size='small'
                          />
                        </TableCell>
                        <TableCell>
                          <Button
                            size='small'
                            variant='contained'
                            sx={{ mr: 1 }}
                            onClick={() => {
                              setForm({
                                ...item,
                                currentStock: item.currentStock,
                                minStockAlert: item.minStockAlert,
                              });
                              setEditOpen(true);
                            }}>
                            <EditIcon sx={{ fontSize: 14 }} />
                          </Button>
                          <Button
                            size='small'
                            variant='outlined'
                            color={item.activeStatus ? 'warning' : 'success'}
                            onClick={() => toggle(item.id)}>
                            {item.activeStatus ? 'Deactivate' : 'Activate'}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        maxWidth='sm'
        fullWidth>
        <DialogTitle fontWeight={700}>Edit Store Item</DialogTitle>
        <DialogContent
          sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField
            label='Item Name'
            value={form.storeName}
            onChange={(e) => setForm({ ...form, storeName: e.target.value })}
            fullWidth
          />
          <TextField
            select
            label='Unit'
            value={form.unit}
            onChange={(e) => setForm({ ...form, unit: e.target.value })}
            fullWidth
            SelectProps={{ native: true }}>
            {UNITS.map((u) => (
              <option key={u} value={u}>
                {u}
              </option>
            ))}
          </TextField>
          {form.unit === 'bottle' && (
            <>
              <TextField
                label='Bottle Size *'
                type='number'
                value={form.bottleSize ?? ''}
                onChange={(e) =>
                  setForm({ ...form, bottleSize: e.target.value })
                }
                helperText='Volume per bottle, e.g. 750 ml'
                fullWidth
              />
              <TextField
                select
                label='Bottle Base Unit'
                value={form.bottleUnit || 'ml'}
                onChange={(e) =>
                  setForm({ ...form, bottleUnit: e.target.value })
                }
                fullWidth
                SelectProps={{ native: true }}>
                {BOTTLE_BASE_UNITS.map((u) => (
                  <option key={u} value={u}>
                    {u}
                  </option>
                ))}
              </TextField>
            </>
          )}
          <TextField
            label={`Current Stock${form.unit === 'bottle' ? ' (in bottles)' : ''}`}
            type='number'
            value={form.currentStock}
            onChange={(e) => setForm({ ...form, currentStock: e.target.value })}
            fullWidth
          />
          <TextField
            label='Min Alert Level'
            type='number'
            value={form.minStockAlert}
            onChange={(e) =>
              setForm({ ...form, minStockAlert: e.target.value })
            }
            fullWidth
          />

          {/* Item Master extension fields — shown in edit too */}
          <Divider sx={{ my: 1 }}>
            <Chip label='Item Master Details' size='small' />
          </Divider>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <TextField
              label='Item Code'
              size='small'
              value={form.itemCode || ''}
              onChange={(e) => setForm({ ...form, itemCode: e.target.value })}
              helperText='Auto-generated when blank'
            />
            <TextField
              label='Barcode'
              size='small'
              value={form.barcode || ''}
              onChange={(e) => setForm({ ...form, barcode: e.target.value })}
            />
            <TextField
              label='Purchase Price'
              size='small'
              type='number'
              value={form.purchasePrice ?? ''}
              onChange={(e) =>
                setForm({ ...form, purchasePrice: e.target.value })
              }
              helperText='Auto-updates from latest purchase'
            />
            <TextField
              label='Selling Price'
              size='small'
              type='number'
              value={form.sellingPrice ?? ''}
              onChange={(e) =>
                setForm({ ...form, sellingPrice: e.target.value })
              }
            />
            <TextField
              label='GST %'
              size='small'
              type='number'
              value={form.gstPercent ?? ''}
              onChange={(e) =>
                setForm({ ...form, gstPercent: e.target.value })
              }
            />
            <TextField
              label='HSN Code'
              size='small'
              value={form.hsnCode || ''}
              onChange={(e) => setForm({ ...form, hsnCode: e.target.value })}
            />
            <TextField
              label='Opening Stock'
              size='small'
              type='number'
              value={form.openingStock ?? ''}
              onChange={(e) =>
                setForm({ ...form, openingStock: e.target.value })
              }
              helperText='Captured at item creation'
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button variant='contained' onClick={save} disabled={saving}>
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
